<template>
	<AsiListTableLayout :filter-text="filterModel.fulltext" :has-filter="hasFilter"
	                    @clearFilterText="filterTextChanged(null)" @clearSearch="clearSearch()"
	                    @update:filterText="filterTextChanged">
		<AsiContentContainer no-bottom-margin>
			<v-data-table
				:footer-props="{itemsPerPageOptions: perPageOptions}"
				:headers="createHeaders()"
				:items="items"
				:loading="loadingInternal || loadingDelete"
				:options.sync="tableOptions"
				:server-items-length="total"
				item-key="id"
				multi-sort
				@click:row="openModelDetailPage">

				<template v-slot:item.description="{item}">
					<span>{{
							descriptionTranslatedAndTrimmed(item)
						}}</span>
				</template>
				<template v-if="showActions" v-slot:item.actions="{item}">
					<div class="d-flex flex-row flex-wrap">
						<AsiBtn :disabled="loadingDelete" :icon="icons.fileImage" small @click.native.stop="setUpdateBrandDetailModel(item); imageDialog.open();"/>
						<AsiBtn :disabled="loadingDelete" :icon="icons.edit" small @click.native.stop="setUpdateBrandDetailModel(item); updateDialog.open();"/>
						<AsiBtn :disabled="loadingDelete" :icon="icons.delete" small @click="performDelete(item)"/>
					</div>
				</template>
			</v-data-table>
		</AsiContentContainer>

		<template v-slot:advanced-filters>
			<AsiTextFieldSimple v-model="filterModel.name" :placeholder="$t('brand.name')" clearable/>
			<AsiTextFieldSimple v-model="filterModel.description" :placeholder="$t('brand.description')" clearable/>
		</template>

		<AsiConfirmDialog v-if="showActions" ref="confirm"/>
		<BrandUpdateImageDialog v-if="updateBrandDetailModel" :brand="updateBrandDetailModel" :open="imageDialog.isOpen"
		                        @cancel="imageDialog.close()"
		                        @save="imageDialog.close(); reload(false, false)"/>
		<BrandUpdateDialog v-if="updateBrandDetailModel" :brand="updateBrandDetailModel" :open="updateDialog.isOpen"
		                   @cancel="updateDialog.close()"
		                   @save="updateDialog.close(); reload(false, false)"/>

	</AsiListTableLayout>
</template>

<script lang="ts">
	import {Component, Emit, Prop, Watch} from 'vue-property-decorator';
	import AsiListTableLayout from "@/components/common/AsiListTableLayout.vue";
	import AsiContentContainer from "@/components/common/AsiContentContainer.vue";
	import AsiListTable from "@/components/common/AsiListTable.vue";
	import {IBrandAdmin, IBrandAdminListEntry} from "@/models/brand/BrandAdminModels";
	import IAsiListTableImplementation from "@/components/common/IAsiListTableImplementation";
	import BrandListFilter from "@/models/brand/BrandListFilter";
	import AsiListTableHeader from "@/components/common/AsiListTableHeader";
	import AsiListTableOptions from "@/components/common/AsiListTableOptions";
	import IPaginatedResponse from "@/models/IPaginatedResponse";
	import Icon from "@/plugins/icons";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
	import Snackbar from "@/helpers/Snackbar";
	import AsiDialogAvatarUpload from "@/components/common/AsiDialogAvatarUpload.vue";
	import DialogHandler from "@/components/common/DialogHandler";
	import BrandUpdateImageDialog from "@/components/brand/admin/BrandUpdateImageDialog.vue";
	import BrandUpdateDialog from "@/components/brand/admin/BrandUpdateDialog.vue";

	@Component({
		components: {
			BrandUpdateDialog,
			BrandUpdateImageDialog,
			AsiDialogAvatarUpload, AsiConfirmDialog, AsiContentContainer, AsiListTableLayout}
	})
	export default class BrandList extends AsiListTable<IBrandAdminListEntry, BrandListFilter> implements IAsiListTableImplementation<IBrandAdminListEntry, BrandListFilter> {

		@Prop({type: Boolean, default: false})
		public showActions!: boolean;

		private icons = Icon;
		private initialized: boolean = false;
		private loadingDelete: boolean = false;
		private updateBrandDetailModel: IBrandAdmin | null = null;

		private updateDialog: DialogHandler = new DialogHandler();
		private imageDialog: DialogHandler = new DialogHandler();

		public created(): void {
			this.reload(false, false);
			this.$nextTick(() => this.initialized = true);
		}

		public createFilterModel(): BrandListFilter {
			return new BrandListFilter();
		}

		public openModelDetailPage(): void {
			//do nothing...
		}

		public createHeaders(): AsiListTableHeader[] {
			const ret: AsiListTableHeader[] = [
				new AsiListTableHeader(this.$t('brand.name'), 'name', true, true, 'start'),
				new AsiListTableHeader(this.$t('brand.description'), 'description', true, true, 'start')
			];

			if (this.showActions) {
				ret.push(
					new AsiListTableHeader('', 'actions', false, false, 'end', '9rem')
				);
			}

			return ret;
		}

		public fetchData(filterModel: BrandListFilter | null, tableOptions: AsiListTableOptions | null): Promise<IPaginatedResponse<IBrandAdminListEntry>> {
			return this.$brandServiceAdmin.brands(filterModel, tableOptions);
		}

		public filterTextChanged(filterText: string): void {
			if (this.filterModel === null) return;
			this.filterModel.fulltext = filterText;
		}

		public reload(goToFirstPage: boolean = false, debounce: boolean = true): void {
			this.reloadInternal(this, goToFirstPage, debounce);
		}

		@Emit('changed')
		public changed(id: string): string {
			return id;
		}

		@Emit('deleted')
		public deleted(id: string): string {
			return id;
		}

		protected prepareOptions(options: AsiListTableOptions): AsiListTableOptions {
			if (options.sortBy.length === 0 && this.defaultSortBy.length === 0) {
				options.sortBy = ['name'];
				options.sortDesc = [false];
			}
			return options;
		}

		private descriptionTranslatedAndTrimmed(brand: IBrandAdminListEntry): string | null {
			if (brand.description) {
				var translated = TranslatedValueHelper.get(brand.description, this.$i18n.locale);
				if (translated && translated.length > 50) {
					return translated ? translated.substring(0, 50) + '...' : null;
				}
				return translated;
			}
			return null;
		}

		@Watch('tableOptions', {deep: true})
		private onTableOptionsChanged(): void {
			if (!this.initialized) return;
			this.reload();
		}

		@Watch('filterModel', {deep: true})
		private onFilterModelChanged(): void {
			if (!this.initialized) return;
			this.reload(true);
		}

		private async performDelete(entry: IBrandAdminListEntry): Promise<void> {
			const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
			confirm.openDialog().then((res: boolean) => {
				if (!res) return;

				const id = entry.id;
				this.loadingDelete = true;
				this.$brandServiceAdmin.delete(id)
					.then(() => {
						this.reload(false, false);
						this.deleted(id);
					})
					.catch(() => Snackbar.deleteError())
					.finally(() => this.loadingDelete = false);
			});
		}

		private async setUpdateBrandDetailModel(entry: IBrandAdminListEntry): Promise<void> {
			const id = entry.id;
			this.updateBrandDetailModel = await this.$brandServiceAdmin.brand(id);
		}
	}
</script>

<style scoped lang="scss">

</style>
